/**
 * UI stylesheet — handwritten film-noir CSS injected as a <style> element
 * owned by the UI module. No frameworks, no network fetches (grain is an
 * inline SVG data URI). Owned by UI.
 */

const STYLE_ELEMENT_ID = 'gum-ui-styles'

/** Typewriter stack — system fonts only, no downloads. */
const TYPE_FONT = "'Courier Prime', 'Courier New', Courier, 'Nimbus Mono PS', monospace"

const INK = '#08080a'
const PAPER = '#ddd5c4'
const PAPER_DIM = 'rgba(221, 213, 196, 0.55)'
const BLOOD = '#a31621'
const PANEL_BG = 'rgba(8, 8, 11, 0.92)'
const PANEL_EDGE = 'rgba(221, 213, 196, 0.28)'

/** Procedural grain tile: inline SVG turbulence, URL-encoded, zero fetches. */
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E\")"

const CSS = `
/* ---------- shared ---------- */
.gum-hidden { display: none !important; }

.gum-grain {
  position: absolute;
  inset: -60px;
  pointer-events: none;
  background-image: ${GRAIN_URI};
  opacity: 0.16;
  mix-blend-mode: overlay;
  animation: gum-grain-jitter 0.42s steps(5) infinite;
}

@keyframes gum-grain-jitter {
  0% { transform: translate(0, 0); }
  20% { transform: translate(-22px, 14px); }
  40% { transform: translate(14px, -26px); }
  60% { transform: translate(-10px, -8px); }
  80% { transform: translate(24px, 18px); }
  100% { transform: translate(0, 0); }
}

@keyframes gum-blink {
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
}

/* ---------- title card (#title-card content is UI-owned) ---------- */
#title-card.gum-title {
  /* Pool of light pulled slightly off-axis (upper-left); corners crush to black.
     A soft warm (2800K) volumetric pool sits behind the wordmark so the title
     reads as lit by an unseen streetlamp, not printed on flat navy. */
  background:
    radial-gradient(ellipse 130% 110% at 50% 50%, transparent 30%, rgba(0, 0, 0, 0.92) 100%),
    radial-gradient(ellipse 60% 42% at 50% 46%, rgba(122, 92, 48, 0.16), transparent 70%),
    radial-gradient(ellipse at 42% 36%, rgba(38, 43, 58, 0.6), transparent 62%),
    ${INK};
  overflow: hidden;
}
/* Breathing vignette — corners darken ~35%, slow 8s swell. */
#title-card .gum-title-vignette {
  position: absolute;
  inset: -4%;
  pointer-events: none;
  background: radial-gradient(ellipse 95% 85% at 50% 50%, transparent 42%, rgba(0, 0, 0, 0.55) 100%);
  animation: gum-vignette-breathe 8s ease-in-out infinite alternate;
}
@keyframes gum-vignette-breathe {
  from { transform: scale(1); }
  to { transform: scale(1.03); }
}
/* One faint desaturated cyan neon accent bleeding in from a corner —
   a promise of the world beyond the card. */
#title-card .gum-title-accent {
  position: absolute;
  right: -12%;
  bottom: -16%;
  width: 55vw;
  height: 45vh;
  pointer-events: none;
  background: radial-gradient(ellipse at 75% 85%, rgba(96, 148, 160, 0.09), transparent 65%);
}
/* Venetian-blind shadow raking diagonally across the card, drifting slowly. */
#title-card .gum-title-blinds {
  position: absolute;
  inset: -25%;
  pointer-events: none;
  background: repeating-linear-gradient(
    120deg,
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 0) 46px,
    rgba(0, 0, 0, 0.55) 58px,
    rgba(0, 0, 0, 0.55) 88px,
    rgba(0, 0, 0, 0) 100px
  );
  opacity: 0.35;
  animation: gum-blinds-drift 26s linear infinite alternate;
}
@keyframes gum-blinds-drift {
  from { transform: translateX(0); }
  to { transform: translateX(72px); }
}
/* Sparse rain streaks behind the type — the case is rain-soaked. */
#title-card .gum-title-rain {
  position: absolute;
  inset: -120px;
  pointer-events: none;
  opacity: 0.14;
  background-image:
    repeating-linear-gradient(
      100deg,
      transparent 0px,
      transparent 38px,
      rgba(186, 203, 228, 0.55) 38px,
      rgba(186, 203, 228, 0.55) 39px,
      transparent 39px,
      transparent 71px
    ),
    repeating-linear-gradient(
      97deg,
      transparent 0px,
      transparent 53px,
      rgba(186, 203, 228, 0.35) 53px,
      rgba(186, 203, 228, 0.35) 54px,
      transparent 54px,
      transparent 107px
    );
  animation: gum-rain-fall 0.9s linear infinite;
}
/* Far parallax rain layer — slower, fainter, different spacing so the
   streaks read as depth planes instead of wallpaper banding. */
#title-card .gum-title-rain-far {
  opacity: 0.06;
  background-image:
    repeating-linear-gradient(
      103deg,
      transparent 0px,
      transparent 23px,
      rgba(186, 203, 228, 0.5) 23px,
      rgba(186, 203, 228, 0.5) 24px,
      transparent 24px,
      transparent 59px
    );
  animation-duration: 1.7s;
}
/* Near parallax rain layer — faster and brighter. */
#title-card .gum-title-rain-near {
  opacity: 0.12;
  background-image:
    repeating-linear-gradient(
      96deg,
      transparent 0px,
      transparent 89px,
      rgba(196, 212, 235, 0.7) 89px,
      rgba(196, 212, 235, 0.7) 91px,
      transparent 91px,
      transparent 163px
    );
  animation-duration: 0.55s;
}
@keyframes gum-rain-fall {
  from { transform: translateY(-110px); }
  to { transform: translateY(0); }
}
/* Title grain must read on near-black: screen blend, regenerating jitter. */
#title-card .gum-grain {
  mix-blend-mode: screen;
  opacity: 0.09;
}
/* Drifting fog plane behind the wordmark. */
#title-card .gum-title-fog {
  position: absolute;
  width: 140vw;
  height: 70vh;
  left: -20vw;
  top: 20vh;
  pointer-events: none;
  background: radial-gradient(ellipse at 50% 50%, rgba(140, 152, 178, 0.07), transparent 65%);
  filter: blur(8px);
  animation: gum-fog-drift 30s ease-in-out infinite alternate;
}
@keyframes gum-fog-drift {
  from { transform: translate(-6vw, 1vh) scale(1); }
  to { transform: translate(6vw, -2vh) scale(1.15); }
}
#title-card .gum-title-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 6vw;
  animation: gum-gate-weave 2.1s ease-in-out infinite;
}
@keyframes gum-gate-weave {
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(0.6px, -0.8px); }
  50% { transform: translate(-0.7px, 0.5px); }
  75% { transform: translate(0.4px, 0.9px); }
}
#title-card .gum-title-overline {
  font-family: ${TYPE_FONT};
  font-size: 0.85rem;
  letter-spacing: 0.55em;
  color: rgba(221, 213, 196, 0.68);
  margin: 0 0 2.2rem;
}
#title-card .gum-title-name {
  font-family: ${TYPE_FONT};
  font-weight: 700;
  font-size: clamp(3.2rem, 12vw, 9rem);
  letter-spacing: 0.32em;
  margin-right: -0.32em;
  /* Lit, not printed: top-down falloff across the letterforms. */
  background: linear-gradient(180deg, #f0e8d6 0%, ${PAPER} 45%, #a89f8c 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 18px rgba(221, 213, 196, 0.25))
    drop-shadow(3px 3px 0 rgba(0, 0, 0, 0.85));
  animation: gum-marquee-flicker 7.3s steps(1) infinite;
}
/* Failing-marquee flicker: mostly steady, occasional 2-frame dropouts. */
@keyframes gum-marquee-flicker {
  0%, 41.9%, 43.1%, 67.9%, 68.6%, 100% { opacity: 1; }
  42%, 43% { opacity: 0.82; }
  68%, 68.5% { opacity: 0.74; }
}
#title-card .gum-title-rule {
  width: min(420px, 60vw);
  height: 1px;
  margin: 2.4rem 0;
  background: linear-gradient(90deg, transparent, ${PAPER_DIM}, transparent);
}
#title-card .gum-title-tagline {
  font-family: ${TYPE_FONT};
  /* The one actionable element on screen — must be legible (>=16px). */
  font-size: 1.05rem;
  letter-spacing: 0.34em;
  color: #b8b0a0;
  margin: 0;
  animation: gum-cta-pulse 2s ease-in-out infinite;
}
@keyframes gum-cta-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* ---------- crosshair + interact prompt ---------- */
.gum-crosshair {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  margin: -3px 0 0 -3px;
  border-radius: 50%;
  background: rgba(221, 213, 196, 0.35);
  transition: transform 120ms ease, background 120ms ease, box-shadow 120ms ease;
}
.gum-crosshair.gum-crosshair-hot {
  background: #ffffff;
  transform: scale(1.6);
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.55);
}
.gum-interact-prompt {
  position: absolute;
  left: 50%;
  top: calc(50% + 30px);
  transform: translateX(-50%);
  font-family: ${TYPE_FONT};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.26em;
  color: #ffffff;
  text-shadow: 0 1px 3px #000;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(221, 213, 196, 0.3);
  border-radius: 999px;
  padding: 0.45rem 1.1rem 0.45rem 1.36rem;
  white-space: nowrap;
}

/* ---------- monologue bar (bottom third) ---------- */
.gum-monologue {
  position: absolute;
  left: 50%;
  bottom: 11vh;
  transform: translateX(-50%);
  width: min(72ch, 88vw);
  padding: 1.1rem 1.6rem;
  /* Near-grayscale chrome with one desaturated red accent — the red is a
     border, never a fill, so it can't fight the monochrome grade. */
  background: rgba(0, 0, 0, 0.82);
  border: 1px solid #8a2020;
  border-left: 3px solid #8a2020;
}
.gum-monologue-text {
  font-family: ${TYPE_FONT};
  font-size: 1.25rem;
  line-height: 1.55;
  letter-spacing: 0.04em;
  color: #f5f0e1;
  text-shadow: 0 1px 2px #000;
  margin: 0;
  min-height: 1.55em;
}
.gum-monologue-text::after {
  content: '\\258C';
  margin-left: 2px;
  color: ${PAPER_DIM};
  animation: gum-blink 0.9s steps(1) infinite;
}

/* ---------- zone label + toasts ---------- */
.gum-zone-label {
  position: absolute;
  top: 28px;
  left: 32px;
  font-family: ${TYPE_FONT};
  font-size: 0.78rem;
  letter-spacing: 0.5em;
  color: ${PAPER};
  text-shadow: 0 1px 3px #000;
  opacity: 0;
}
.gum-zone-label.gum-zone-show {
  animation: gum-zone-fade 3.2s ease forwards;
}
@keyframes gum-zone-fade {
  0% { opacity: 0; transform: translateY(-4px); }
  12% { opacity: 0.9; transform: translateY(0); }
  75% { opacity: 0.9; }
  100% { opacity: 0; }
}
.gum-toasts {
  position: absolute;
  top: 28px;
  right: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}
.gum-toast {
  font-family: ${TYPE_FONT};
  font-size: 0.74rem;
  letter-spacing: 0.22em;
  color: ${PAPER};
  background: ${PANEL_BG};
  border: 1px solid ${PANEL_EDGE};
  border-left: 3px solid ${BLOOD};
  padding: 0.55rem 0.9rem;
  animation: gum-toast-in 240ms ease;
}
@keyframes gum-toast-in {
  from { opacity: 0; transform: translateX(18px); }
  to { opacity: 1; transform: translateX(0); }
}

/* ---------- panels (journal + accuse) ---------- */
.gum-panel {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(880px, 86vw);
  max-height: 78vh;
  overflow-y: auto;
  background: ${PANEL_BG};
  border: 1px solid ${PANEL_EDGE};
  box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.45), 0 24px 80px rgba(0, 0, 0, 0.8);
  padding: 2rem 2.4rem 1.6rem;
  pointer-events: auto;
  color: ${PAPER};
  font-family: ${TYPE_FONT};
}
.gum-panel-title {
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.42em;
  margin: 0 0 0.4rem;
  border-bottom: 1px solid ${PANEL_EDGE};
  padding-bottom: 0.8rem;
}
.gum-panel-sub {
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  color: ${PAPER_DIM};
  margin: 0.8rem 0 0;
}
.gum-panel-footer {
  margin-top: 1.6rem;
  padding-top: 0.9rem;
  border-top: 1px solid ${PANEL_EDGE};
  font-size: 0.68rem;
  letter-spacing: 0.28em;
  color: ${PAPER_DIM};
  text-align: center;
}

/* ---------- journal ---------- */
.gum-journal-columns {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2rem;
  margin-top: 1.4rem;
}
.gum-journal-heading {
  font-size: 0.78rem;
  letter-spacing: 0.4em;
  color: ${BLOOD};
  margin: 0 0 0.9rem;
}
.gum-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}
.gum-clue-name {
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin: 0;
}
.gum-clue-desc {
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${PAPER_DIM};
  margin: 0.25rem 0 0;
}
.gum-empty {
  font-size: 0.8rem;
  font-style: italic;
  color: ${PAPER_DIM};
}
.gum-suspect-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  border: 1px solid ${PANEL_EDGE};
  padding: 0.6rem 0.8rem;
}
.gum-suspect-name {
  font-size: 0.85rem;
  letter-spacing: 0.12em;
}
.gum-button {
  font-family: ${TYPE_FONT};
  font-size: 0.7rem;
  letter-spacing: 0.26em;
  color: ${PAPER};
  background: transparent;
  border: 1px solid ${BLOOD};
  padding: 0.45rem 0.8rem 0.45rem 1.05rem;
  cursor: pointer;
  pointer-events: auto;
  transition: background 120ms ease, color 120ms ease;
}
.gum-button:hover {
  background: ${BLOOD};
  color: #fff;
}
.gum-button-quiet {
  border-color: ${PANEL_EDGE};
}
.gum-button-quiet:hover {
  background: ${PAPER};
  color: ${INK};
}
.gum-accuse-list {
  margin-top: 1.4rem;
}
.gum-accuse-actions {
  margin-top: 1.6rem;
  text-align: right;
}

/* ---------- end cards (smash-cut) ---------- */
.gum-end {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 8vw;
  pointer-events: auto;
  overflow: hidden;
}
.gum-end-won { background: ${PAPER}; color: ${INK}; }
.gum-end-lost { background: ${INK}; color: ${PAPER}; }
.gum-stamp {
  font-family: ${TYPE_FONT};
  font-weight: 700;
  font-size: clamp(2.4rem, 8vw, 6rem);
  letter-spacing: 0.3em;
  margin-right: -0.3em;
  padding: 0.4em 0.7em 0.4em 1em;
  border: 6px double currentColor;
  transform: rotate(-7deg);
  animation: gum-stamp-in 180ms cubic-bezier(0.2, 1.6, 0.4, 1) both;
}
.gum-end-won .gum-stamp { color: ${BLOOD}; }
.gum-end-lost .gum-stamp { color: rgba(221, 213, 196, 0.85); }
@keyframes gum-stamp-in {
  from { transform: rotate(-7deg) scale(2.4); opacity: 0; }
  to { transform: rotate(-7deg) scale(1); opacity: 1; }
}
.gum-end-headline {
  font-family: ${TYPE_FONT};
  font-size: clamp(1rem, 3vw, 1.7rem);
  letter-spacing: 0.3em;
  margin: 3rem 0 0;
}
.gum-end-sub {
  font-family: ${TYPE_FONT};
  font-size: 0.85rem;
  letter-spacing: 0.14em;
  opacity: 0.65;
  margin: 1.2rem 0 0;
  max-width: 60ch;
}
#gum-touch-journal {
  position: fixed;
  top: calc(0.9rem + env(safe-area-inset-top));
  right: calc(0.9rem + env(safe-area-inset-right));
  z-index: 30;
  padding: 0.55rem 0.9rem;
  font-family: ${TYPE_FONT};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  color: #d8c9a3;
  background: rgba(10, 10, 12, 0.72);
  border: 1px solid rgba(216, 184, 96, 0.45);
  border-radius: 2px;
  -webkit-tap-highlight-color: transparent;
}
#gum-touch-journal:active {
  background: rgba(216, 184, 96, 0.25);
}
`

/** Append the UI-owned <style> element once. */
export const injectStyles = (): void => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ELEMENT_ID
  style.textContent = CSS
  document.head.append(style)
}
